import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  togglePlayPause,
  setPlayState,
  setCurrentAudioIndex,
  toggleRepeatMode,
  shuffleAudioLinks,
  toggleShowAudioLinks,
  setShowFooter,
  setAudioId,
  setCurrentTime,
  setDuration,
  setAudioLinks,
  setAudioListId,
} from "../../Redux/AudioSlice";
import "./PlayAudioSlider.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBackwardStep,
  faForwardStep,
  faPause,
  faPlay,
  faRotateRight,
  faShuffle,
} from "@fortawesome/free-solid-svg-icons";
import { Badge, Button } from "antd";
import throttle from "lodash.throttle";
import TrackItem from "../TrackItem/TrackItem";

function PlayAudioSlider() {
  const audioRef = useRef(null);
  const sliderRef = useRef(null);
  const animationRef = useRef(null);
  const isPlaying = useSelector((state) => state.audio.isPlaying);
  const repeatMode = useSelector((state) => state.audio.repeatMode);
  const audioLinks = useSelector((state) => state.audio.audioLinks);
  const showAudioLinks = useSelector((state) => state.audio.showAudioLinks);
  const currentAudioIndex = useSelector(
    (state) => state.audio.currentAudioIndex
  );
  const currentAudioId = useSelector((state) => state.audio.audioId);
  const currentTime = useSelector((state) => state.audio.currentTime);
  const duration = useSelector((state) => state.audio.duration);
  const dispatch = useDispatch();

  const audioSrc = audioLinks[currentAudioIndex]?.link || "/audio-test.mp3";
  const audioId = audioLinks[currentAudioIndex]?.audioId || null;

  useEffect(() => {
    if (audioLinks.length > 0 && audioId !== currentAudioId) {
      if (audioRef.current) {
        audioRef.current.src = audioSrc;
        audioRef.current.load(); // Load new source only when it changes
        dispatch(setCurrentTime(0)); // Reset playback position
      }
    }
    dispatch(setAudioId(audioId));
  }, [audioId, audioLinks]);

  useEffect(() => {
    if (isPlaying) {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Playback error:", error);
        });
      }
      animationRef.current = requestAnimationFrame(updateSlider);
    } else {
      audioRef.current.pause();
      cancelAnimationFrame(animationRef.current);
    }
    return () => cancelAnimationFrame(animationRef.current);
  }, [isPlaying, audioId]);

  const updateSlider = () => {
    if (audioRef.current) {
      sliderRef.current.value = audioRef.current.currentTime;
      throttledSetCurrentTime(audioRef.current.currentTime);
      animationRef.current = requestAnimationFrame(updateSlider);
    }
  };

  const throttledSetCurrentTime = throttle(
    (time) => dispatch(setCurrentTime(time)),
    200 // Dispatch only once every 200ms
  );

  const handleSliderChange = (e) => {
    const newTime = parseFloat(e.target.value);
    audioRef.current.currentTime = newTime;
    dispatch(setCurrentTime(newTime));
    if (!isPlaying) {
      dispatch(setPlayState(true)); // Ensure playback resumes
    }
  };

  const handlePlayPause = () => {
    dispatch(togglePlayPause());
  };

  const handleMovePrevious = () => {
    if (currentAudioIndex > 0) {
      dispatch(setCurrentAudioIndex(currentAudioIndex - 1));
      dispatch(setAudioId(audioId));
      dispatch(setPlayState(true));
    }
  };

  const handleMoveNext = () => {
    if (currentAudioIndex < audioLinks.length - 1) {
      dispatch(setCurrentAudioIndex(currentAudioIndex + 1));
      dispatch(setAudioId(audioId));
      dispatch(setPlayState(true));
    }
  };

  const handleRepeatMode = () => {
    dispatch(toggleRepeatMode());
  };

  const handleShuffle = () => {
    dispatch(shuffleAudioLinks());
  };

  const handleShowAudioLinks = () => {
    dispatch(toggleShowAudioLinks());
  };

  const handleAudioEnd = () => {
    if (repeatMode === "infinite") {
      audioRef.current.currentTime = 0;
      dispatch(setCurrentTime(0));
      audioRef.current.play();
      animationRef.current = requestAnimationFrame(updateSlider);
    } else if (repeatMode === "one-time") {
      audioRef.current.currentTime = 0;
      dispatch(setCurrentTime(0));
      audioRef.current.play();
      dispatch(setPlayState(true));
      dispatch(toggleRepeatMode());
      dispatch(toggleRepeatMode());
      animationRef.current = requestAnimationFrame(updateSlider);
    } else {
      if (currentAudioIndex < audioLinks.length - 1) {
        dispatch(setCurrentAudioIndex(currentAudioIndex + 1));
        dispatch(setPlayState(true));
      } else {
        dispatch(setPlayState(false));
        dispatch(setCurrentTime(audioRef.current.duration)); // Keep slider at the end
        dispatch(setShowFooter(false)); // hidden footer after finish
        dispatch(setAudioLinks([]));
        dispatch(setAudioListId(null));
        dispatch(setCurrentAudioIndex(0));
      }
    }
    cancelAnimationFrame(animationRef.current);
  };

  const formatTime = (time) => {
    if (!time) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        onLoadedMetadata={() =>
          dispatch(setDuration(audioRef.current.duration))
        }
        onEnded={handleAudioEnd}
        src={audioSrc}
      ></audio>
      <div className="controls">
        <div className="controlBtns">
          <Button
            shape="circle"
            color="default"
            variant="text"
            onClick={handleMovePrevious}
            disabled={audioLinks.length <= 1 || currentAudioIndex === 0}
          >
            <FontAwesomeIcon icon={faBackwardStep} />
          </Button>
          <Button
            shape="circle"
            color="default"
            variant="text"
            onClick={handlePlayPause}
            title="play/pause"
          >
            {isPlaying ? (
              <FontAwesomeIcon icon={faPause} />
            ) : (
              <FontAwesomeIcon icon={faPlay} />
            )}
          </Button>
          <Button
            shape="circle"
            color="default"
            variant="text"
            onClick={handleMoveNext}
            disabled={
              audioLinks.length <= 1 ||
              currentAudioIndex === audioLinks.length - 1
            }
          >
            <FontAwesomeIcon icon={faForwardStep} />
          </Button>
          <Button
            onClick={handleShuffle}
            shape="circle"
            color="default"
            variant="text"
            title="shuffle"
          >
            <FontAwesomeIcon icon={faShuffle} />
          </Button>
          <Badge
            count={repeatMode === "one-time" ? 1 : 0}
            size="small"
            style={{
              top: "7px",
              right: "7px",
              borderColor: "transparent",
            }}
          >
            <Button
              shape="circle"
              color="default"
              variant="text"
              style={{ color: repeatMode === "off" ? "black" : "white" }}
              onClick={handleRepeatMode}
              title="repeat"
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </Button>
          </Badge>
        </div>
        <div className="time">{formatTime(currentTime)}</div>
        <input
          className="slider"
          ref={sliderRef}
          type="range"
          min="0"
          max={duration || 1}
          step="0.1"
          value={currentTime}
          onChange={handleSliderChange}
        />

        <div className="time">{formatTime(duration)}</div>
        <TrackItem
          trackName={audioLinks[currentAudioIndex].name}
          artistName={audioLinks[currentAudioIndex].author}
          img={
            audioLinks[currentAudioIndex].trackImg ||
            "https://picsum.photos/200/300"
          }
          isFromFooter={true}
          handleShowAudioLinks={handleShowAudioLinks}
          showAudioLinks={showAudioLinks}
          audioLink={audioSrc}
        />
      </div>
    </div>
  );
}

export default PlayAudioSlider;
